import JSZip from "jszip";
import {
  Archive,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Download,
  Eye,
  FileImage,
  FolderOpen,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Pause,
  Play,
  Printer,
  RotateCcw,
  Save,
  ScanSearch,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Trash2,
  UploadCloud,
  Wrench,
  X,
} from "lucide-react";
import { FormEvent, InputHTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import { FixTextModal, QaReport } from "./fix-text-modal";

const STORAGE_KEY = "gettr-translate-batch-v1";
const MAX_BYTES = 3_000_000;
const ZIP_SIZE = 50;
const PAGE_SIZE = 100;
const DEFAULT_CONCURRENCY = 4;
const MAX_CONCURRENCY = 10;
const MAX_THROTTLE_RETRIES = 8;

type ItemStatus = "ready" | "uploading" | "queued" | "translating" | "collecting" | "done" | "error";

type BatchItem = {
  id: string;
  name: string;
  path: string;
  size: number;
  type: "image/png" | "image/jpeg";
  status: ItemStatus;
  originalStored?: boolean;
  outputStored?: boolean;
  documentId?: string;
  documentKey?: string;
  error?: string;
  billedCharacters?: number;
  qaStatus?: "unchecked" | "checking" | "passed" | "needs_review" | "error";
  qaIssueCount?: number;
  qaError?: string;
  qaCheckedAt?: string;
};

type BatchState = {
  id: string;
  name?: string | null;
  createdAt: string;
  items: BatchItem[];
};

type Session = { configured: boolean; authenticated: boolean };

type BatchSummary = {
  id: string;
  name?: string | null;
  createdAt: string;
  updatedAt: string;
  total: number;
  done: number;
  error: number;
};

type TextExport = {
  id: string;
  account: string;
  rangeStart: string;
  rangeEnd: string;
  status: "queued" | "translating" | "complete" | "failed";
  totalRows: number;
  translatedRows: number;
  failedRows: number;
  mediaTotal: number;
  mediaStored: number;
  mediaFailed: number;
};

type TextExportReportEntry = {
  posted_at_utc: string;
  x_post_url: string;
  original_text_zh: string;
  english_translation: string;
  linked_urls: string;
  hashtags: string;
  mentions: string;
  post_type: string;
  translation_status: string;
  translation_error: string;
  images: Array<{ url: string; altText: string }>;
};

function reportMonths(entry: TextExport): string[] {
  const cursor = new Date(entry.rangeStart);
  cursor.setUTCDate(1);
  const end = new Date(entry.rangeEnd);
  const months: string[] = [];
  while (cursor < end) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

function imageProgress(entry: TextExport): string {
  if (!entry.mediaTotal) return "";
  const suffix = entry.mediaStored < entry.mediaTotal ? " · copying" : "";
  const failed = entry.mediaFailed ? ` · ${entry.mediaFailed} failed` : "";
  return ` · ${entry.mediaStored}/${entry.mediaTotal} images${suffix}${failed}`;
}

type BackfillResponse = {
  done: boolean;
  phase?: "originals" | "outputs";
  cursor?: string;
  imported: number;
};

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

class PausedError extends Error {}

async function apiJson<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, options);
  let body: { error?: string; code?: string; retryAfterSeconds?: number } = {};
  try {
    body = await response.json() as typeof body;
  } catch {
    // A failed proxy request may not have a JSON body.
  }
  if (!response.ok) {
    throw new ApiError(body.error || `Request failed (${response.status})`, response.status, body.code, body.retryAfterSeconds);
  }
  return body as T;
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function jitter(milliseconds: number): number {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return Math.floor((value[0] / 0xffffffff) * milliseconds);
}

function isDeepLThrottle(error: unknown): error is ApiError {
  return error instanceof ApiError && ["deepl_rate_limited", "deepl_overloaded", "deepl_server_error"].includes(error.code ?? "");
}

function formatBytes(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1_000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function archivePath(path: string): string {
  return path.replaceAll("\\", "/").split("/").filter((part) => part && part !== "." && part !== "..").join("/");
}

function safeDownloadName(name: string | null | undefined): string {
  const safe = (name || "translated")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .trim()
    .slice(0, 80);
  return safe || "translated";
}

function translatedPath(path: string): string {
  const safe = archivePath(path);
  const slash = safe.lastIndexOf("/");
  const folder = slash >= 0 ? safe.slice(0, slash + 1) : "";
  const filename = slash >= 0 ? safe.slice(slash + 1) : safe;
  const dot = filename.lastIndexOf(".");
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  const extension = filename.toLowerCase().endsWith(".png") ? ".png" : ".jpg";
  return `${folder}${stem}_en${extension}`;
}

function outputUrl(batchId: string, item: BatchItem): string {
  return `/api/output/${batchId}/${item.id}.${item.type === "image/png" ? "png" : "jpg"}`;
}

function originalUrl(batchId: string, item: BatchItem): string {
  return `/api/original/${batchId}/${item.id}.${item.type === "image/png" ? "png" : "jpg"}`;
}

function canvasBlob(canvas: HTMLCanvasElement, type: BatchItem["type"], quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Browser could not encode the enhanced image")), type, quality);
  });
}

async function loadImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`Could not load image (${response.status})`);
  return response.blob();
}

async function prepareEnhancedImage(batchId: string, item: BatchItem): Promise<{ file: File; width: number; height: number }> {
  const source = await loadImageBlob(`${originalUrl(batchId, item)}?v=${Date.now()}`);
  const bitmap = await createImageBitmap(source);
  const width = bitmap.width;
  const height = bitmap.height;
  const maxPixels = 24_000_000;
  const maximumScale = Math.sqrt(maxPixels / Math.max(1, width * height));
  const startingScale = Math.min(2, maximumScale);
  if (startingScale <= 1) {
    bitmap.close();
    throw new Error("This image is already too large to enhance safely in the browser");
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    throw new Error("Browser image enhancement is unavailable");
  }

  try {
    for (const requestedScale of [startingScale, 1.75, 1.5, 1.25]) {
      const scale = Math.min(startingScale, requestedScale);
      if (scale <= 1) continue;
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await canvasBlob(canvas, item.type, item.type === "image/jpeg" ? 0.94 : undefined);
      if (blob.size <= MAX_BYTES) return { file: new File([blob], item.name, { type: item.type }), width, height };
    }
  } finally {
    bitmap.close();
    canvas.width = 1;
    canvas.height = 1;
  }
  throw new Error("The enhanced image exceeds DeepL's 3 MB image limit");
}

async function resizeTranslatedImage(source: Blob, type: BatchItem["type"], width: number, height: number): Promise<File> {
  const bitmap = await createImageBitmap(source);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    throw new Error("Browser image resizing is unavailable");
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await canvasBlob(canvas, type, type === "image/jpeg" ? 0.94 : undefined);
  canvas.width = 1;
  canvas.height = 1;
  if (blob.size > MAX_BYTES) throw new Error("The final enhanced image exceeds the 3 MB storage limit");
  return new File([blob], `enhanced.${type === "image/png" ? "png" : "jpg"}`, { type });
}

function readSavedBatch(): BatchState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BatchState;
    if (!parsed.id || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function statusLabel(item: BatchItem): string {
  switch (item.status) {
    case "ready": return item.error || "Ready";
    case "uploading": return "Uploading to DeepL";
    case "queued": return "Queued at DeepL";
    case "translating": return "Translating";
    case "collecting": return "Saving result";
    case "done": return "Complete";
    case "error": return item.error || "Failed";
  }
}

function StatusIcon({ item }: { item: BatchItem }) {
  if (["uploading", "queued", "translating", "collecting"].includes(item.status)) {
    return <LoaderCircle size={16} className="spin" />;
  }
  if (item.status === "done") return <Check size={16} />;
  if (item.status === "error" || item.error) return <CircleAlert size={16} />;
  return <FileImage size={16} />;
}

function QaBadge({ item }: { item: BatchItem }) {
  if (item.status !== "done") return null;
  if (item.qaStatus === "checking") return <span className="qa-badge checking"><LoaderCircle size={11} className="spin" /> Checking</span>;
  if (item.qaStatus === "passed") return <span className="qa-badge passed"><Check size={11} /> Checked</span>;
  if (item.qaStatus === "needs_review") return <span className="qa-badge review"><ShieldAlert size={11} /> {item.qaIssueCount || 1} issue{item.qaIssueCount === 1 ? "" : "s"}</span>;
  if (item.qaStatus === "error") return <span className="qa-badge error"><CircleAlert size={11} /> Check failed</span>;
  return <span className="qa-badge unchecked">Not checked</span>;
}

function PinGate({ configured, onAuthenticated }: { configured: boolean; onAuthenticated: () => void }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiJson("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gate-shell">
      <section className="gate-card">
        <div className="brand-mark"><span>G</span></div>
        <div className="eyebrow"><LockKeyhole size={14} /> Private utility</div>
        <h1>Image Translator</h1>
        <p>Chinese screenshots in. English screenshots out.</p>
        {!configured ? (
          <div className="notice error-notice">
            <CircleAlert size={18} />
            <div><strong>Setup required</strong><span>Add the Worker PIN, session secret, and DeepL API key.</span></div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="pin">Access PIN</label>
            <div className="pin-field">
              <KeyRound size={18} />
              <input id="pin" type="password" autoFocus autoComplete="current-password" value={pin} onChange={(event) => setPin(event.target.value)} placeholder="Enter PIN" />
            </div>
            {error && <div className="form-error">{error}</div>}
            <button className="button primary wide" disabled={!pin || busy}>
              {busy ? <LoaderCircle size={18} className="spin" /> : <ShieldCheck size={18} />}
              Continue
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

function TextExportReport({ exportId, month }: { exportId: string; month: string }) {
  const [report, setReport] = useState<{ account: string; entries: TextExportReportEntry[] } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void apiJson<{ account: string; entries: TextExportReportEntry[] }>(`/api/text-exports/${exportId}/report/${month}`)
      .then(setReport)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Could not load report"));
  }, [exportId, month]);
  useEffect(() => {
    if (!report) return;
    const previous = document.title;
    document.title = `@${report.account} · ${month} · X Archive`;
    return () => { document.title = previous; };
  }, [report, month]);
  if (error) return <main className="loading-screen"><div className="notice error-notice"><CircleAlert size={18} />{error}</div></main>;
  if (!report) return <main className="loading-screen"><LoaderCircle className="spin" /></main>;
  const monthLabel = new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  return (
    <main className="text-report">
      <header className="text-report-header">
        <div><div className="eyebrow accent">Private X archive</div><h1>@{report.account} · {monthLabel}</h1><p>{report.entries.length.toLocaleString()} posts · Original Chinese, English translation, and available images</p></div>
        <div className="report-actions"><a className="button secondary" href="/">Back to translator</a><button className="button primary" onClick={() => window.print()}><Printer size={17} /> Print / Save PDF</button><span className="print-note">In the print dialog, turn off “Headers and footers.”</span></div>
      </header>
      <div className="text-report-list">
        {report.entries.map((entry) => (
          <article className="text-report-entry" key={entry.x_post_url}>
            <div className="text-report-meta"><time>{new Date(entry.posted_at_utc).toLocaleString(undefined, { timeZone: "UTC", dateStyle: "medium", timeStyle: "short" })} UTC</time><a href={entry.x_post_url} target="_blank" rel="noreferrer">View on X</a>{entry.post_type !== "original" && <span>{entry.post_type}</span>}</div>
            <section><h2>Chinese</h2><p>{entry.original_text_zh}</p></section>
            <section><h2>English</h2>{entry.translation_status === "pending" ? <p className="muted">Translation pending.</p> : entry.translation_status === "failed" ? <p className="error-copy">{entry.translation_error || "Translation failed."}</p> : <p>{entry.english_translation}</p>}</section>
            {entry.images.length > 0 && <div className="text-report-images">{entry.images.map((image) => <img src={image.url} alt={image.altText || "Attached X image"} key={image.url} />)}</div>}
            {entry.linked_urls && <div className="text-report-links"><strong>Links</strong>{entry.linked_urls.split('; ').map((link) => <a href={link} target="_blank" rel="noreferrer" key={link}>{link}</a>)}</div>}
          </article>
        ))}
      </div>
    </main>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [batch, setBatch] = useState<BatchState | null>(() => readSavedBatch());
  const [sharedBatches, setSharedBatches] = useState<BatchSummary[]>([]);
  const [textExports, setTextExports] = useState<TextExport[]>([]);
  const [syncingR2, setSyncingR2] = useState<{ scanned: number } | null>(null);
  const [concurrency, setConcurrency] = useState(DEFAULT_CONCURRENCY);
  const [adaptiveConcurrency, setAdaptiveConcurrency] = useState(DEFAULT_CONCURRENCY);
  const [throttleNotice, setThrottleNotice] = useState("");
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState<{ current: number; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const [globalError, setGlobalError] = useState("");
  const [preview, setPreview] = useState<{ item: BatchItem; view: "original" | "output" } | null>(null);
  const [qaRunning, setQaRunning] = useState(false);
  const [qaProgress, setQaProgress] = useState<{ current: number; total: number } | null>(null);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [fixEditor, setFixEditor] = useState<{ item: BatchItem; report: QaReport } | null>(null);
  const [fixLoadingId, setFixLoadingId] = useState<string | null>(null);
  const [batchNameSaving, setBatchNameSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const files = useRef(new Map<string, File>());
  const paused = useRef(false);
  const sharedInitialized = useRef(false);
  const adaptiveLimit = useRef(DEFAULT_CONCURRENCY);
  const completedSinceThrottle = useRef(0);

  useEffect(() => {
    apiJson<Session>("/api/session").then(setSession).catch(() => setSession({ configured: false, authenticated: false }));
  }, []);

  useEffect(() => {
    if (!session?.authenticated || sharedInitialized.current) return;
    sharedInitialized.current = true;
    void initializeSharedLibrary();
  }, [session?.authenticated]);

  useEffect(() => {
    if (!session?.authenticated) return;
    const refresh = () => void apiJson<{ exports: TextExport[] }>("/api/text-exports").then(({ exports }) => setTextExports(exports)).catch(() => undefined);
    refresh();
    const timer = window.setInterval(refresh, 15_000);
    return () => window.clearInterval(timer);
  }, [session?.authenticated]);

  useEffect(() => {
    if (!session?.authenticated) return;
    const refresh = () => void refreshSharedBatches(false).catch(() => undefined);
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const timer = window.setInterval(refresh, 15_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [session?.authenticated]);

  useEffect(() => {
    if (batch) localStorage.setItem(STORAGE_KEY, JSON.stringify(batch));
    else localStorage.removeItem(STORAGE_KEY);
  }, [batch]);

  const counts = useMemo(() => {
    const result = { total: 0, ready: 0, active: 0, done: 0, error: 0 };
    for (const item of batch?.items ?? []) {
      result.total += 1;
      if (item.status === "ready") result.ready += 1;
      if (["uploading", "queued", "translating", "collecting"].includes(item.status)) result.active += 1;
      if (item.status === "done") result.done += 1;
      if (item.status === "error") result.error += 1;
    }
    return result;
  }, [batch]);

  const progress = counts.total ? Math.round(((counts.done + counts.error) / counts.total) * 100) : 0;
  const filteredItems = useMemo(() => issuesOnly ? (batch?.items ?? []).filter((item) => item.qaStatus === "needs_review") : (batch?.items ?? []), [batch, issuesOnly]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const qaCounts = useMemo(() => {
    const completed = (batch?.items ?? []).filter((item) => item.status === "done");
    return {
      eligible: completed.length,
      checked: completed.filter((item) => item.qaStatus === "passed" || item.qaStatus === "needs_review").length,
      issues: completed.filter((item) => item.qaStatus === "needs_review").length,
    };
  }, [batch]);

  useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  function updateItem(id: string, update: Partial<BatchItem>) {
    setBatch((current) => current ? {
      ...current,
      items: current.items.map((item) => item.id === id ? { ...item, ...update } : item),
    } : current);
  }

  async function checkOneTranslation(item: BatchItem): Promise<QaReport> {
    if (!batch) throw new Error("No batch selected");
    updateItem(item.id, { qaStatus: "checking", qaError: undefined });
    try {
      const report = await apiJson<QaReport>(`/api/item/${batch.id}/${item.id}/qa`, { method: "POST" });
      updateItem(item.id, {
        qaStatus: report.status,
        qaIssueCount: report.issues.length,
        qaCheckedAt: report.checkedAt,
        qaError: undefined,
      });
      return report;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Translation check failed";
      updateItem(item.id, { qaStatus: "error", qaError: message });
      throw caught;
    }
  }

  async function checkTranslations() {
    if (!batch || running || qaRunning) return;
    const queue = batch.items.filter((item) => item.status === "done" && !["passed", "needs_review"].includes(item.qaStatus ?? "unchecked"));
    if (!queue.length) return;
    setQaRunning(true);
    setQaProgress({ current: 0, total: queue.length });
    setGlobalError("");
    let index = 0;
    let completed = 0;
    const worker = async () => {
      while (index < queue.length) {
        const item = queue[index++];
        try { await checkOneTranslation(item); } catch { /* Individual failures remain visible on the row. */ }
        completed += 1;
        setQaProgress({ current: completed, total: queue.length });
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(3, queue.length) }, () => worker()));
      await refreshSharedBatches(false);
    } finally {
      setQaRunning(false);
      setQaProgress(null);
    }
  }

  async function openFixText(item: BatchItem) {
    if (!batch || item.status !== "done") return;
    setFixLoadingId(item.id);
    setGlobalError("");
    try {
      let report: QaReport;
      try {
        report = await apiJson<QaReport>(`/api/item/${batch.id}/${item.id}/qa`);
      } catch (caught) {
        if (!(caught instanceof ApiError) || caught.code !== "qa_not_found") throw caught;
        report = await checkOneTranslation(item);
      }
      updateItem(item.id, {
        qaStatus: report.status,
        qaIssueCount: report.issues.filter((issue) => !issue.dismissed).length,
        qaError: undefined,
      });
      setFixEditor({ item, report });
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not open Fix Text");
    } finally {
      setFixLoadingId(null);
    }
  }

  async function refreshSharedBatches(loadLatest = false) {
    const result = await apiJson<{ batches: BatchSummary[] }>("/api/batches");
    setSharedBatches(result.batches);
    const liveBatchIds = new Set(result.batches.map((entry) => entry.id));
    setBatch((current) => {
      if (!current || liveBatchIds.has(current.id)) return current;
      const wasStored = current.items.some((item) => item.originalStored || item.outputStored);
      if (wasStored) files.current.clear();
      return wasStored ? null : current;
    });
    if (loadLatest && result.batches[0]) await openSharedBatch(result.batches[0].id);
  }

  async function openSharedBatch(batchId: string) {
    setGlobalError("");
    try {
      const result = await apiJson<{ batch: BatchState }>(`/api/batch/${batchId}`);
      setBatch(result.batch);
      setPage(0);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "not_found") {
        setSharedBatches((current) => current.filter((entry) => entry.id !== batchId));
        setBatch((current) => current?.id === batchId ? null : current);
        setGlobalError("This batch was deleted by another user.");
      } else {
        setGlobalError(caught instanceof Error ? caught.message : "Could not open shared batch");
      }
    }
  }

  async function saveBatchName() {
    if (!batch) return;
    setBatchNameSaving(true);
    setGlobalError("");
    try {
      const result = await apiJson<{ name: string | null }>(`/api/batch/${batch.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: batch.name ?? "" }),
      });
      setBatch((current) => current ? { ...current, name: result.name } : current);
      await refreshSharedBatches(false);
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not save the batch name");
    } finally {
      setBatchNameSaving(false);
    }
  }

  async function syncExistingR2(force = false) {
    setSyncingR2({ scanned: 0 });
    let phase: "originals" | "outputs" = "originals";
    let cursor: string | undefined;
    let scanned = 0;
    let first = true;
    try {
      for (let pageNumber = 0; pageNumber < 1_000; pageNumber += 1) {
        const result: BackfillResponse = await apiJson<BackfillResponse>("/api/backfill", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phase, cursor, force: force && first }),
        });
        first = false;
        scanned += result.imported;
        setSyncingR2({ scanned });
        if (result.done) break;
        phase = result.phase ?? phase;
        cursor = result.cursor;
      }
    } finally {
      setSyncingR2(null);
    }
  }

  async function initializeSharedLibrary() {
    try {
      await syncExistingR2(false);
      await refreshSharedBatches(!readSavedBatch());
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not load the shared library");
    }
  }

  async function forceR2Sync() {
    setGlobalError("");
    try {
      await syncExistingR2(true);
      await refreshSharedBatches(!batch);
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not sync existing R2 files");
    }
  }

  function createNewBatch() {
    paused.current = true;
    setBatch(null);
    setPage(0);
    files.current.clear();
  }

  function addFiles(selected: File[]) {
    if (!selected.length) return;
    const batchId = batch?.id || `batch_${crypto.randomUUID()}`;
    const existing = new Set((batch?.items ?? []).map((item) => `${item.path}:${item.size}`));
    const additions: BatchItem[] = [];
    for (const file of selected) {
      const path = file.webkitRelativePath || file.name;
      const duplicateKey = `${path}:${file.size}`;
      if (existing.has(duplicateKey)) {
        const existingItem = batch?.items.find((item) => `${item.path}:${item.size}` === duplicateKey);
        if (existingItem) files.current.set(existingItem.id, file);
        continue;
      }
      existing.add(duplicateKey);
      const id = `img_${crypto.randomUUID()}`;
      const supported = file.type === "image/png" || file.type === "image/jpeg";
      const withinLimit = file.size <= MAX_BYTES;
      const item: BatchItem = {
        id,
        name: file.name,
        path,
        size: file.size,
        type: file.type === "image/png" ? "image/png" : "image/jpeg",
        status: supported && withinLimit ? "ready" : "error",
        originalStored: false,
        error: !supported ? "Only PNG and JPEG are supported" : !withinLimit ? "Larger than DeepL's 3 MB limit" : undefined,
      };
      additions.push(item);
      if (supported && withinLimit) files.current.set(id, file);
    }
    setBatch((current) => ({
      id: current?.id || batchId,
      name: current?.name,
      createdAt: current?.createdAt || new Date().toISOString(),
      items: [...(current?.items ?? []), ...additions],
    }));
  }

  function setAdaptiveLimit(limit: number) {
    const next = Math.max(1, Math.min(concurrency, limit));
    adaptiveLimit.current = next;
    setAdaptiveConcurrency(next);
  }

  function recordCompletedTranslation() {
    completedSinceThrottle.current += 1;
    if (adaptiveLimit.current < concurrency && completedSinceThrottle.current >= 5) {
      completedSinceThrottle.current = 0;
      const next = adaptiveLimit.current + 1;
      setAdaptiveLimit(next);
      setThrottleNotice(next < concurrency
        ? `DeepL recovered — increasing to ${next} active jobs.`
        : `DeepL recovered — back to ${concurrency} active jobs.`);
    }
  }

  async function withAdaptiveRetry<T>(request: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt += 1) {
      if (paused.current) throw new PausedError("Batch paused");
      try {
        return await request();
      } catch (caught) {
        if (!isDeepLThrottle(caught) || attempt >= MAX_THROTTLE_RETRIES || paused.current) throw caught;
        completedSinceThrottle.current = 0;
        const reduced = Math.max(1, Math.floor(adaptiveLimit.current / 2));
        setAdaptiveLimit(reduced);
        const exponentialSeconds = Math.min(60, 2 ** (attempt + 1));
        const waitSeconds = Math.max(exponentialSeconds, caught.retryAfterSeconds ?? 0);
        setThrottleNotice(`DeepL is busy — reduced to ${reduced} active job${reduced === 1 ? "" : "s"} and retrying in ${waitSeconds}s.`);
        await sleep(waitSeconds * 1_000 + jitter(1_000));
      }
    }
  }

  async function processItem(item: BatchItem, batchId: string, enhanced = false): Promise<boolean> {
    let documentId = item.documentId;
    let documentKey = item.documentKey;
    let originalDimensions: { width: number; height: number } | undefined;
    try {
      if (!item.originalStored) {
        const file = files.current.get(item.id);
        if (!file) {
          updateItem(item.id, { status: "error", error: "Reselect this file to upload its original" });
          return false;
        }
        updateItem(item.id, { status: "uploading", error: undefined });
        const upload = new FormData();
        upload.set("file", file, item.name);
        upload.set("batchId", batchId);
        upload.set("assetId", item.id);
        upload.set("path", item.path);
        await apiJson("/api/upload", { method: "POST", body: upload });
        item = { ...item, originalStored: true };
        updateItem(item.id, { originalStored: true, status: "ready" });
      }
      if (!documentId || !documentKey) {
        updateItem(item.id, { status: "queued", error: undefined });
        let translateRequest: RequestInit;
        if (enhanced) {
          updateItem(item.id, { status: "uploading", error: undefined });
          const prepared = await prepareEnhancedImage(batchId, item);
          originalDimensions = { width: prepared.width, height: prepared.height };
          const form = new FormData();
          form.set("file", prepared.file, item.name);
          form.set("batchId", batchId);
          form.set("assetId", item.id);
          form.set("filename", item.name);
          translateRequest = { method: "POST", body: form };
        } else {
          translateRequest = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ batchId, assetId: item.id, filename: item.name, type: item.type }),
          };
        }
        const submitted = await withAdaptiveRetry(() => apiJson<{ documentId: string; documentKey: string }>("/api/translate", translateRequest));
        documentId = submitted.documentId;
        documentKey = submitted.documentKey;
        updateItem(item.id, { documentId, documentKey, status: "queued" });
      }

      while (!paused.current) {
        const status = await withAdaptiveRetry(() => apiJson<{ status: "queued" | "translating" | "done" | "error"; secondsRemaining?: number; billedCharacters?: number; error?: string }>("/api/status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ documentId, documentKey, batchId, assetId: item.id }),
        }));
        if (status.status === "error") throw new Error(status.error || "DeepL could not translate this image");
        if (status.status === "done") {
          updateItem(item.id, { status: "collecting", billedCharacters: status.billedCharacters });
          await withAdaptiveRetry(() => apiJson("/api/collect", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documentId, documentKey, batchId, assetId: item.id, filename: item.name }),
          }));
          if (enhanced && originalDimensions) {
            const translated = await loadImageBlob(`${outputUrl(batchId, item)}?v=${Date.now()}`);
            const finalFile = await resizeTranslatedImage(translated, item.type, originalDimensions.width, originalDimensions.height);
            const form = new FormData();
            form.set("file", finalFile, finalFile.name);
            await apiJson(`/api/item/${batchId}/${item.id}/enhanced-output`, { method: "POST", body: form });
          }
          updateItem(item.id, { status: "done", error: undefined, billedCharacters: status.billedCharacters });
          return true;
        }
        updateItem(item.id, { status: status.status });
        await sleep(Math.max(3, Math.min(status.secondsRemaining || 5, 12)) * 1_000);
      }
      return false;
    } catch (caught) {
      if (caught instanceof PausedError) return false;
      const message = caught instanceof Error ? caught.message : "Translation failed";
      updateItem(item.id, { status: "error", error: message, documentId, documentKey });
      void apiJson(`/api/item/${batchId}/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "error", error: message }),
      }).catch(() => undefined);
      return false;
    }
  }

  async function start() {
    if (!batch || running) return;
    const batchId = batch.id;
    paused.current = false;
    adaptiveLimit.current = concurrency;
    setAdaptiveConcurrency(concurrency);
    completedSinceThrottle.current = 0;
    setThrottleNotice("");
    setRunning(true);
    setGlobalError("");
    const queue = batch.items.filter((item) => item.status !== "done" && item.status !== "error");
    try {
      await apiJson("/api/glossary/sync", { method: "POST" });
      await new Promise<void>((resolve) => {
        let index = 0;
        let active = 0;
        const launch = () => {
          while (!paused.current && active < adaptiveLimit.current && index < queue.length) {
            const item = queue[index++];
            active += 1;
            void processItem(item, batchId).then((completed) => {
              if (completed) recordCompletedTranslation();
            }).finally(() => {
              active -= 1;
              if ((paused.current || index >= queue.length) && active === 0) resolve();
              else launch();
            });
          }
          if ((paused.current || index >= queue.length) && active === 0) resolve();
        };
        launch();
      });
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not prepare the translation glossary");
    } finally {
      setRunning(false);
      void refreshSharedBatches(false).catch(() => undefined);
    }
  }

  function pause() {
    paused.current = true;
    setRunning(false);
  }

  function retryFailed() {
    setBatch((current) => current ? {
      ...current,
      items: current.items.map((item) => item.status === "error"
        ? { ...item, status: item.documentId ? "queued" : "ready", error: undefined }
        : item),
    } : current);
  }

  async function redoTranslation(item: BatchItem, enhanced = false) {
    if (!batch || running || !item.originalStored) return;
    const batchId = batch.id;
    paused.current = false;
    adaptiveLimit.current = concurrency;
    setAdaptiveConcurrency(concurrency);
    setThrottleNotice("");
    setGlobalError("");
    setRunning(true);
    if (preview?.item.id === item.id) setPreview(null);
    try {
      await apiJson("/api/glossary/sync", { method: "POST" });
      await apiJson(`/api/item/${batchId}/${item.id}/redo`, { method: "POST" });
      const resetItem: BatchItem = {
        ...item,
        status: "ready",
        outputStored: false,
        documentId: undefined,
        documentKey: undefined,
        error: undefined,
        billedCharacters: undefined,
      };
      updateItem(item.id, resetItem);
      await processItem(resetItem, batchId, enhanced);
      await refreshSharedBatches(false);
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : enhanced ? "Could not redo enhanced translation" : "Could not redo translation");
    } finally {
      setRunning(false);
    }
  }

  async function downloadZips() {
    if (!batch) return;
    const completed = batch.items.filter((item) => item.status === "done");
    if (!completed.length) return;
    setGlobalError("");
    setExporting({ current: 0, total: completed.length });
    try {
      for (let offset = 0; offset < completed.length; offset += ZIP_SIZE) {
        const zip = new JSZip();
        const chunk = completed.slice(offset, offset + ZIP_SIZE);
        for (let index = 0; index < chunk.length; index += 1) {
          const item = chunk[index];
          const [originalResponse, englishResponse] = await Promise.all([
            fetch(originalUrl(batch.id, item)),
            fetch(outputUrl(batch.id, item)),
          ]);
          if (!originalResponse.ok) throw new Error(`Could not download the Chinese original for ${item.path}`);
          if (!englishResponse.ok) throw new Error(`Could not download the English image for ${item.path}`);
          const [original, english] = await Promise.all([originalResponse.blob(), englishResponse.blob()]);
          zip.file(`original-chinese/${archivePath(item.path)}`, original, { compression: "STORE" });
          zip.file(`english/${translatedPath(item.path)}`, english, { compression: "STORE" });
          setExporting({ current: offset + index + 1, total: completed.length });
        }
        const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const part = String(offset / ZIP_SIZE + 1).padStart(3, "0");
        const baseName = safeDownloadName(batch.name);
        link.download = completed.length > ZIP_SIZE ? `${baseName}-${part}.zip` : `${baseName}.zip`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(link.href), 30_000);
      }
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "ZIP export failed");
    } finally {
      setExporting(null);
    }
  }

  async function deleteBatch() {
    if (!batch || !window.confirm("Delete every original and translated image stored for this batch? This cannot be undone.")) return;
    setGlobalError("");
    try {
      await apiJson(`/api/batch/${batch.id}`, { method: "DELETE" });
      files.current.clear();
      setBatch(null);
      await refreshSharedBatches(true);
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not delete batch");
    }
  }

  async function deleteItem(item: BatchItem) {
    if (!batch || !window.confirm(`Delete ${item.path} and its translated version from R2?`)) return;
    try {
      await apiJson(`/api/item/${batch.id}/${item.id}`, { method: "DELETE" });
      files.current.delete(item.id);
      setBatch((current) => current ? { ...current, items: current.items.filter((entry) => entry.id !== item.id) } : current);
      void refreshSharedBatches(false).catch(() => undefined);
      if (preview?.item.id === item.id) setPreview(null);
    } catch (caught) {
      setGlobalError(caught instanceof Error ? caught.message : "Could not delete image");
    }
  }

  async function logout() {
    await apiJson("/api/logout", { method: "POST" });
    setSession((current) => current ? { ...current, authenticated: false } : current);
  }

  if (!session) return <main className="loading-screen"><LoaderCircle className="spin" /></main>;
  if (!session.authenticated) return <PinGate configured={session.configured} onAuthenticated={() => setSession({ configured: true, authenticated: true })} />;
  const reportMatch = window.location.pathname.match(/^\/report\/([a-zA-Z0-9_-]{8,120})\/([0-9]{4}-[0-9]{2})$/);
  if (reportMatch) return <TextExportReport exportId={reportMatch[1]} month={reportMatch[2]} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup"><div className="brand-mark small"><span>G</span></div><div><strong>Image Translator</strong><span>Internal utility</span></div></div>
        <div className="header-actions"><span className="secure-chip"><ShieldCheck size={14} /> PIN protected</span><button className="icon-button" onClick={logout} title="Sign out"><LogOut size={18} /></button></div>
      </header>

      <main className="workspace">
        <section className="hero-row">
          <div>
            <div className="eyebrow accent">DeepL image translation · Beta</div>
            <h1>Chinese screenshots,<br /><span>translated in place.</span></h1>
            <p>Select individual files or an entire folder. The page processes a few at a time and preserves your filenames and folder structure.</p>
          </div>
          <div className="language-card">
            <div><span className="language-code">ZH</span><span>Chinese</span></div>
            <div className="language-line"><span></span><ChevronDown size={16} /></div>
            <div><span className="language-code bright">EN-US</span><span>English (US)</span></div>
          </div>
        </section>

        <section className="upload-card">
          <input ref={fileInput} className="hidden" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" multiple onChange={(event) => addFiles(Array.from(event.target.files ?? []))} />
          <input ref={folderInput} className="hidden" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" multiple {...({ webkitdirectory: "", directory: "" } as InputHTMLAttributes<HTMLInputElement>)} onChange={(event) => addFiles(Array.from(event.target.files ?? []))} />
          <div className="upload-icon"><UploadCloud size={28} /></div>
          <div><h2>Add screenshots</h2><p>PNG or JPEG · maximum 3 MB each</p></div>
          <div className="upload-actions">
            <button className="button secondary" onClick={() => fileInput.current?.click()}><FileImage size={17} /> Select images</button>
            <button className="button primary" onClick={() => folderInput.current?.click()}><FolderOpen size={17} /> Select folder</button>
          </div>
        </section>

        <section className="library-bar">
          <div className="library-copy">
            <Archive size={18} />
            <div><strong>Shared batch library</strong><span>{sharedBatches.length.toLocaleString()} stored batch{sharedBatches.length === 1 ? "" : "es"} · visible to everyone with the PIN</span></div>
          </div>
          <div className="library-actions">
            <select
              aria-label="Open a shared batch"
              value={sharedBatches.some((entry) => entry.id === batch?.id) ? batch?.id : ""}
              onChange={(event) => event.target.value && void openSharedBatch(event.target.value)}
              disabled={running || Boolean(syncingR2)}
            >
              <option value="">Select stored batch</option>
              {sharedBatches.map((entry) => (
                <option value={entry.id} key={entry.id}>
                  {entry.name ? `${entry.name} · ` : ""}{new Date(entry.createdAt).toLocaleString()} · {entry.done}/{entry.total} complete
                </option>
              ))}
            </select>
            <button className="button secondary" onClick={forceR2Sync} disabled={running || Boolean(syncingR2)}>
              {syncingR2 ? <LoaderCircle size={17} className="spin" /> : <RotateCcw size={17} />}
              {syncingR2 ? `Scanning ${syncingR2.scanned.toLocaleString()}` : "Sync R2"}
            </button>
            <button className="button secondary" onClick={createNewBatch} disabled={running}>New batch</button>
          </div>
        </section>

        {textExports.length > 0 && (
          <section className="library-bar text-export-library">
            <div className="library-copy">
              <Archive size={18} />
              <div><strong>X text exports</strong><span>Private CSV exports translated through DeepL</span></div>
            </div>
            <div className="text-export-list">
              {textExports.map((entry) => (
                <div className="text-export-actions" key={entry.id}>
                  <a className="button secondary" href={`/api/text-exports/${entry.id}/download`}><Download size={16} /> @{entry.account} CSV · {entry.translatedRows}/{entry.totalRows}{imageProgress(entry)}</a>
                  {reportMonths(entry).map((month) => <a className="button secondary" href={`/report/${entry.id}/${month}`} target="_blank" rel="noreferrer" key={month}><Printer size={16} /> {month} report</a>)}
                </div>
              ))}
            </div>
          </section>
        )}

        {!batch && globalError && <div className="notice error-notice standalone-notice"><CircleAlert size={18} /><span>{globalError}</span><button onClick={() => setGlobalError("")}><X size={16} /></button></div>}

        {batch && batch.items.length > 0 && (
          <section className="batch-card">
            <div className="batch-heading">
              <div className="batch-title-block">
                <div className="eyebrow">Current batch</div>
                <div className="batch-name-editor">
                  <input
                    aria-label="Batch name"
                    value={batch.name ?? ""}
                    maxLength={100}
                    placeholder="Name this batch"
                    onChange={(event) => setBatch((current) => current ? { ...current, name: event.target.value } : current)}
                    onKeyDown={(event) => { if (event.key === "Enter") void saveBatchName(); }}
                  />
                  <button className="icon-button" title="Save batch name" disabled={batchNameSaving} onClick={() => void saveBatchName()}>
                    {batchNameSaving ? <LoaderCircle size={17} className="spin" /> : <Save size={17} />}
                  </button>
                </div>
                <h2>{counts.total.toLocaleString()} image{counts.total === 1 ? "" : "s"}</h2>
              </div>
              <div className="batch-controls">
                <label className="concurrency">Max at once<select value={concurrency} onChange={(event) => setConcurrency(Number(event.target.value))} disabled={running || qaRunning}>{Array.from({ length: MAX_CONCURRENCY }, (_, index) => index + 1).map((value) => <option value={value} key={value}>{value}</option>)}</select></label>
                {running ? (
                  <button className="button secondary" onClick={pause}><Pause size={17} /> Pause</button>
                ) : (
                  <button className="button primary" onClick={start} disabled={counts.done === counts.total || qaRunning}><Play size={17} /> {counts.done || counts.active ? "Resume" : "Start translation"}</button>
                )}
              </div>
            </div>

            <div className="progress-block">
              <div className="progress-copy"><span>{progress}% complete</span><span>{running ? `Adaptive: ${adaptiveConcurrency}/${concurrency} active · ` : ""}{counts.done.toLocaleString()} done · {counts.error.toLocaleString()} failed</span></div>
              <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
            </div>

            {throttleNotice && <div className="notice throttle-notice"><LoaderCircle size={18} className={running && adaptiveConcurrency < concurrency ? "spin" : ""} /><span>{throttleNotice}</span><button onClick={() => setThrottleNotice("")}><X size={16} /></button></div>}
            {globalError && <div className="notice error-notice"><CircleAlert size={18} /><span>{globalError}</span><button onClick={() => setGlobalError("")}><X size={16} /></button></div>}

            {counts.done > 0 && (
              <div className="qa-toolbar">
                <div><ScanSearch size={17} /><span><strong>{qaCounts.checked.toLocaleString()}</strong> of {qaCounts.eligible.toLocaleString()} checked · <strong className={qaCounts.issues ? "issue-count" : ""}>{qaCounts.issues.toLocaleString()}</strong> need review</span></div>
                <button className={`qa-filter ${issuesOnly ? "active" : ""}`} onClick={() => { setIssuesOnly((value) => !value); setPage(0); }} disabled={!qaCounts.issues && !issuesOnly}>Issues only</button>
              </div>
            )}

            <div className="item-list">
              {pageItems.map((item) => (
                <div className={`item-row status-${item.status}`} key={item.id}>
                  <div className="thumb"><FileImage size={20} /></div>
                  <div className="item-name"><strong title={item.path}>{item.path}</strong><span className="item-meta">{formatBytes(item.size)} <QaBadge item={item} /></span></div>
                  <div className="item-status"><StatusIcon item={item} /><span>{statusLabel(item)}</span></div>
                  <div className="item-actions">
                    <button className="icon-button" disabled={!item.originalStored && item.status !== "done"} onClick={() => setPreview({ item, view: item.status === "done" ? "output" : "original" })} title="Preview"><Eye size={17} /></button>
                    <a className={`icon-button ${!item.originalStored && item.status !== "done" ? "disabled" : ""}`} href={item.status === "done" ? outputUrl(batch.id, item) : originalUrl(batch.id, item)} download title={item.status === "done" ? "Download English image" : "Download original"}><Download size={17} /></a>
                    <button className={`icon-button qa-icon ${item.qaStatus === "needs_review" ? "has-issue" : ""}`} disabled={running || qaRunning || item.status !== "done" || item.qaStatus === "checking"} onClick={() => void checkOneTranslation(item).catch((caught) => setGlobalError(caught instanceof Error ? caught.message : "Translation check failed"))} title="Check translation with Google Vision">{item.qaStatus === "checking" ? <LoaderCircle size={17} className="spin" /> : <ScanSearch size={17} />}</button>
                    <button className="icon-button fix-icon" disabled={running || qaRunning || item.status !== "done" || fixLoadingId === item.id} onClick={() => void openFixText(item)} title="Fix translated text">{fixLoadingId === item.id ? <LoaderCircle size={17} className="spin" /> : <Wrench size={17} />}</button>
                    <button className="icon-button" disabled={running || qaRunning || !item.originalStored || ["uploading", "queued", "translating", "collecting"].includes(item.status)} onClick={() => void redoTranslation(item)} title="Redo translation"><RotateCcw size={17} /></button>
                    <button className="icon-button enhanced-icon" disabled={running || qaRunning || !item.originalStored || ["uploading", "queued", "translating", "collecting"].includes(item.status)} onClick={() => void redoTranslation(item, true)} title="Redo enhanced: translate a 2× image, then restore the original size"><Sparkles size={17} /></button>
                    <button className="icon-button danger-icon" disabled={qaRunning} onClick={() => deleteItem(item)} title="Delete from R2"><Trash2 size={17} /></button>
                  </div>
                </div>
              ))}
              {pageCount > 1 && (
                <div className="list-pagination">
                  <button className="icon-button" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}><ChevronLeft size={17} /></button>
                  <span>Files {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, filteredItems.length).toLocaleString()} of {filteredItems.length.toLocaleString()}</span>
                  <button className="icon-button" disabled={page + 1 >= pageCount} onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}><ChevronRight size={17} /></button>
                </div>
              )}
            </div>

            <div className="batch-footer">
              <div>
                {counts.error > 0 && <button className="text-button" onClick={retryFailed}><RotateCcw size={15} /> Retry failures</button>}
                <button className="text-button danger" onClick={deleteBatch}><Trash2 size={15} /> Delete batch</button>
              </div>
              <div className="footer-actions">
                <button className="button secondary" disabled={!counts.done || running || qaRunning || qaCounts.checked >= qaCounts.eligible} onClick={checkTranslations}>
                  {qaRunning ? <LoaderCircle size={18} className="spin" /> : <ScanSearch size={18} />}
                  {qaProgress ? `Checking ${qaProgress.current}/${qaProgress.total}` : `Check ${Math.max(0, qaCounts.eligible - qaCounts.checked).toLocaleString()} translations`}
                </button>
                <button className="button export" disabled={!counts.done || Boolean(exporting) || qaRunning} onClick={downloadZips}>
                  {exporting ? <LoaderCircle size={18} className="spin" /> : <Archive size={18} />}
                  {exporting ? `Preparing ${exporting.current}/${exporting.total} pairs` : `Download ${counts.done.toLocaleString()} pairs as ZIPs`}
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="privacy-note">
          <LockKeyhole size={17} />
          <div><strong>Private, shared library</strong><span>Files stay in private R2; D1 stores their shared batch metadata so every signed-in user sees the same library.</span></div>
        </section>
      </main>
      {preview && batch && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setPreview(null)}>
          <section className="preview-modal" role="dialog" aria-modal="true" aria-label={`Preview ${preview.item.path}`}>
            <header>
              <div><div className="eyebrow">Image preview</div><strong title={preview.item.path}>{preview.item.path}</strong></div>
              <button className="icon-button" onClick={() => setPreview(null)}><X size={18} /></button>
            </header>
            {preview.item.status === "done" && (
              <div className="preview-tabs">
                <button className={preview.view === "original" ? "active" : ""} onClick={() => setPreview({ ...preview, view: "original" })}>Original</button>
                <button className={preview.view === "output" ? "active" : ""} onClick={() => setPreview({ ...preview, view: "output" })}>English</button>
              </div>
            )}
            <div className="preview-stage">
              <img src={`${preview.view === "output" ? outputUrl(batch.id, preview.item) : originalUrl(batch.id, preview.item)}?inline=1`} alt={`${preview.view === "output" ? "English translation" : "Original"} of ${preview.item.path}`} />
            </div>
            <footer>
              <span>{preview.view === "output" ? "Translated English image" : "Original uploaded image"}</span>
              <a className="button secondary" href={preview.view === "output" ? outputUrl(batch.id, preview.item) : originalUrl(batch.id, preview.item)} download><Download size={17} /> Download</a>
            </footer>
          </section>
        </div>
      )}
      {fixEditor && batch && (
        <FixTextModal
          batchId={batch.id}
          item={fixEditor.item}
          report={fixEditor.report}
          originalSrc={`${originalUrl(batch.id, fixEditor.item)}?inline=1`}
          outputSrc={`${outputUrl(batch.id, fixEditor.item)}?inline=1`}
          onClose={() => setFixEditor(null)}
          onReportUpdated={(report) => {
            const activeIssues = report.issues.filter((issue) => !issue.dismissed).length;
            updateItem(fixEditor.item.id, { qaStatus: report.status, qaIssueCount: activeIssues, qaError: undefined });
            setFixEditor((current) => current ? { ...current, report } : current);
          }}
          onSaved={() => {
            updateItem(fixEditor.item.id, { qaStatus: "unchecked", qaIssueCount: 0, qaCheckedAt: undefined, qaError: undefined });
            setFixEditor(null);
            void refreshSharedBatches(false).catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}
